import { addContactFromAssist } from "./addContactFromAssist";
import { buildApiUrl } from "./apiConfig";
import { findContactForCollege } from "./extractListedContacts";
import { collegeNamesMatch, type ListedCollege } from "./extractListedColleges";

export type AddCollegeStatus = "added" | "already" | "not_found" | "error";

export type AddCollegeFromAssistResult = {
    status: AddCollegeStatus;
    collegeId?: number;
};

export type AttachCollegeContactResult = "saved" | "none";

interface ScorecardCollege {
    id: number;
    name: string;
    city?: string;
    state?: string;
    zip?: string;
    schoolUrl?: string;
    locale?: string;
    size?: number;
}

interface AthleteCollegeRow {
    college?: { id?: number; name?: string };
    id?: number;
    name?: string;
}

function normalize(value: string | undefined): string {
    return (value ?? "").trim().toLowerCase();
}

function pickBestMatch(listed: ListedCollege, results: ScorecardCollege[]): ScorecardCollege | null {
    if (results.length === 0) {
        return null;
    }
    const exact = results.find((college) => normalize(college.name) === normalize(listed.name));
    if (exact) {
        return exact;
    }
    return results.find((college) => collegeNamesMatch(college.name, listed.name)) ?? null;
}

function fallbackCatalogSearchName(name: string): string | null {
    const match = /^(.*?)\b(University|College|Institute|Polytechnic)\b/i.exec(name.trim());
    if (!match) {
        return null;
    }
    const shortened = `${match[1]}${match[2]}`.replace(/\s+/g, " ").trim();
    if (shortened.length < 8 || normalize(shortened) === normalize(name)) {
        return null;
    }
    if (
        /^the\s+(university|college|institute)\b/i.test(shortened) &&
        shortened.split(/\s+/).length <= 2
    ) {
        return null;
    }
    return shortened;
}

async function searchCatalog(name: string): Promise<ScorecardCollege[] | null> {
    const params = new URLSearchParams({ name, page: "0", size: "10" });
    const searchRes = await fetch(buildApiUrl(`/api/colleges?${params.toString()}`), {
        credentials: "include",
        headers: { Accept: "application/json" },
    });
    if (!searchRes.ok) {
        return null;
    }
    const payload: unknown = await searchRes.json();
    return Array.isArray(payload)
        ? (payload as ScorecardCollege[])
        : ((payload as { content?: ScorecardCollege[] }).content ?? []);
}

function collegeIdOnList(listed: ListedCollege, rows: AthleteCollegeRow[]): number | null {
    const wanted = normalize(listed.name);
    const mapped = rows
        .map((row) => ({
            id: row.college?.id ?? row.id,
            name: row.college?.name ?? row.name,
        }))
        .filter((row): row is { id: number; name: string } => row.id != null && Boolean(row.name));
    const exact = mapped.find((row) => normalize(row.name) === wanted);
    if (exact) {
        return exact.id;
    }
    return mapped.find((row) => collegeNamesMatch(row.name, listed.name))?.id ?? null;
}

export async function attachCollegeContactFromAssist(
    username: string,
    listed: ListedCollege,
    collegeId: number,
    answerMarkdown?: string,
    userQuery?: string,
): Promise<AttachCollegeContactResult> {
    if (answerMarkdown) {
        const fromAnswer = findContactForCollege(answerMarkdown, listed.name);
        if (fromAnswer) {
            const result = await addContactFromAssist(username, fromAnswer);
            if (result === "added" || result === "already") {
                return "saved";
            }
        }
    }
    try {
        const meRes = await fetch(buildApiUrl("/api/athletes/me"), {
            credentials: "include",
            headers: { Accept: "application/json" },
        });
        if (!meRes.ok) {
            return "none";
        }
        const me = (await meRes.json()) as { id?: number };
        if (me.id == null) {
            return "none";
        }
        const attachPath = `/api/athletes/${me.id}/colleges/${collegeId}/contacts/from-official-site`;
        const attachQuery =
            userQuery && userQuery.trim()
                ? `?q=${encodeURIComponent(userQuery.trim())}`
                : "";
        const attachRes = await fetch(buildApiUrl(`${attachPath}${attachQuery}`), {
            method: "POST",
            credentials: "include",
            headers: { Accept: "application/json" },
        });
        return attachRes.status === 201 ? "saved" : "none";
    } catch {
        return "none";
    }
}

export async function findAndAddCollegeToAthleteList(
    username: string,
    listed: ListedCollege,
): Promise<AddCollegeFromAssistResult> {
    try {
        const [listRes, catalog] = await Promise.all([
            fetch(buildApiUrl(`/api/colleges/athlete/${encodeURIComponent(username)}`), {
                credentials: "include",
                headers: { Accept: "application/json" },
            }),
            searchCatalog(listed.name),
        ]);
        const existing = listRes.ok ? ((await listRes.json()) as AthleteCollegeRow[]) : [];
        const alreadyId = collegeIdOnList(listed, existing);
        if (alreadyId != null) {
            return { status: "already", collegeId: alreadyId };
        }
        if (catalog == null) {
            return { status: "error" };
        }

        let results = catalog;
        let match = pickBestMatch(listed, results);
        if (!match) {
            const fallbackName = fallbackCatalogSearchName(listed.name);
            if (fallbackName) {
                const fallbackResults = await searchCatalog(fallbackName);
                if (fallbackResults == null) {
                    return { status: "error" };
                }
                results = fallbackResults;
                match = pickBestMatch({ ...listed, name: fallbackName }, results);
            }
        }
        if (!match || match.id == null) {
            return { status: "not_found" };
        }

        const addRes = await fetch(
            buildApiUrl(`/api/colleges/athlete/${encodeURIComponent(username)}`),
            {
                method: "POST",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
                body: JSON.stringify({
                    id: match.id,
                    name: match.name,
                    city: match.city ?? listed.city ?? "",
                    state: match.state ?? listed.state ?? "",
                    zip: match.zip ?? "",
                    schoolUrl: match.schoolUrl ?? "",
                    locale: match.locale ?? "",
                    size: match.size ?? null,
                }),
            },
        );
        if (!addRes.ok) {
            return { status: "error" };
        }
        return { status: "added", collegeId: match.id };
    } catch {
        return { status: "error" };
    }
}
